// @ts-nocheck
/**
 * Coffee Quiz - Interactive quiz to recommend coffee products
 * Features: Multi-step navigation, session storage, GA4 tracking, recommendation engine
 */

/**
 * Bag sizes are sold as separate Shopify products that share a base handle
 * (`cordillera`, `cordillera-2lb`, `cordillera-5lb`) — the same scheme
 * blocks/awp_size_selector.liquid uses on the product page. So the quiz
 * resolves a sibling product for the chosen size rather than looking for a
 * size option on the recommended product's variants.
 */
const QUIZ_SIZE_HANDLE_SUFFIX = {
  '12oz': '',
  '2lb': '-2lb',
  '5lb': '-5lb',
};

/** Sizes smallest-first. */
const QUIZ_SIZE_ORDER = ['12oz', '2lb', '5lb'];

/** Approximate cups per bag — a 12oz bag brews ~19, and the rest scale by weight. */
const QUIZ_CUPS_PER_BAG = {
  '12oz': 19,
  '2lb': 50,
  '5lb': 127,
};

/**
 * Subscription pre-selected for each point on the 1-8 cups/day slider. Each row
 * supplies a little more than the household drinks over the interval while
 * keeping deliveries inside a 3-week freshness window.
 *   cups/day: size x qty every N weeks  (cups supplied / days covered)
 */
const QUIZ_USAGE_PLAN = {
  1: { size: '12oz', quantity: 1, frequency: '3-weeks' }, //  19 / 21
  2: { size: '12oz', quantity: 2, frequency: '3-weeks' }, //  38 / 21
  3: { size: '2lb', quantity: 1, frequency: '2-weeks' },  //  50 / 14
  4: { size: '2lb', quantity: 2, frequency: '3-weeks' },  // 101 / 21
  5: { size: '5lb', quantity: 1, frequency: '3-weeks' },  // 127 / 21
  6: { size: '5lb', quantity: 1, frequency: '3-weeks' },  // 127 / 21
  7: { size: '5lb', quantity: 1, frequency: '2-weeks' },  // 127 / 14
  8: { size: '5lb', quantity: 1, frequency: '2-weeks' },  // 127 / 14
};

class CoffeeQuiz {
  constructor() {
    this.modal = document.getElementById('coffee-quiz-modal');
    
    // Check if email capture is enabled
    this.emailCaptureEnabled = this.modal?.getAttribute('data-email-capture-enabled') !== 'false';
    this.klaviyoListId = this.modal?.getAttribute('data-klaviyo-list-id') || '';

    // When true, limited-time / non-subscription coffees are eligible for recommendation.
    // When false (default), they are excluded so the results page can always push subscriptions.
    this.includeLimitedTime = this.modal?.getAttribute('data-include-limited-time') === 'true';
    this.emailSubmitted = this.hasEmailBeenSubmitted();
    
    // Dynamically detect screens from DOM (supports block reordering in theme editor)
    this.screens = this.detectScreensFromDOM();
    
    this.currentScreenIndex = 0;
    this.answers = this.loadAnswers();
    this.products = [];
    this.recommendedProduct = null;
    // Products backing each size button for the current recommendation, and a
    // handle-keyed cache of size siblings fetched so far.
    this.sizeProducts = {};
    this.sizeProductCache = new Map();
    this.isAdvancing = false; // Debounce flag to prevent double-advancing screens
    this.sliderData = this.loadSliderData();
    this.boundHandlers = {}; // Store bound handlers for cleanup
    
    this.init();
  }

  init() {
    // Only continue with full initialization if modal exists
    if (!this.modal) {
      console.warn('Coffee Quiz: Modal element not found');
      // Still bind open buttons for when modal becomes available
      this.bindOpenButtons();
      return;
    }
    
    this.products = this.loadProducts();
    this.bindOpenButtons();
    this.bindModalEvents();
    this.bindSliderEvents();
    this.bindBodySkipLink();
    this.bindEmailForm();
    this.updateProgress();
    this.restoreState();
  }
  
  /**
   * Detect screens from DOM in their current order
   * This allows screens to be reordered via theme editor blocks
   */
  detectScreensFromDOM() {
    if (!this.modal) {
      // Fallback to default order if modal not found
      return ['intro', 'experience', 'brew-method', 'flavors', 'how-you-take-it', 'body-preference', 'cups-per-day', 'email-capture', 'results'];
    }
    
    const screenElements = this.modal.querySelectorAll('[data-quiz-screen]');
    const screens = [];
    
    screenElements.forEach(el => {
      const screenName = el.getAttribute('data-quiz-screen');
      if (screenName) {
        // Skip email-capture if disabled
        if (screenName === 'email-capture' && !this.emailCaptureEnabled) {
          return;
        }
        screens.push(screenName);
      }
    });
    
    return screens;
  }
  
  /**
   * Load slider configuration data
   */
  loadSliderData() {
    // Simple 1-8 cups slider
    return {
      min: 1,
      max: 8
    };
  }
  
  /**
   * Bind open quiz buttons using event delegation
   */
  bindOpenButtons() {
    // Only bind once globally using a flag on document
    if (document._coffeeQuizOpenBound) return;
    document._coffeeQuizOpenBound = true;
    
    // Use event delegation on document for reliability
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-coffee-quiz]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        // Always get fresh reference to quiz instance
        if (window.coffeeQuiz) {
          window.coffeeQuiz.open();
        }
      }
    });
    
    // Handle hash links (e.g., #coffee-quiz)
    this.bindHashTrigger();
  }
  
  /**
   * Bind hash URL trigger for opening quiz via links like #coffee-quiz
   */
  bindHashTrigger() {
    const QUIZ_HASH = '#coffee-quiz';
    
    // Check hash on page load
    const checkAndOpenFromHash = () => {
      if (window.location.hash === QUIZ_HASH) {
        // Small delay to ensure page is ready
        setTimeout(() => {
          if (window.coffeeQuiz) {
            window.coffeeQuiz.open();
            // Clear the hash without scrolling
            history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }, 100);
      }
    };
    
    // Check on initial load
    checkAndOpenFromHash();
    
    // Listen for hash changes (e.g., clicking a #coffee-quiz link on the same page)
    window.addEventListener('hashchange', () => {
      if (window.location.hash === QUIZ_HASH && window.coffeeQuiz) {
        window.coffeeQuiz.open();
        // Clear the hash without scrolling
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    });
    
    // Also intercept clicks on links with #coffee-quiz href
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href="#coffee-quiz"], a[href$="#coffee-quiz"]');
      if (link) {
        e.preventDefault();
        if (window.coffeeQuiz) {
          window.coffeeQuiz.open();
        }
      }
    });
  }

  /**
   * Load products data from JSON script tag
   */
  loadProducts() {
    const dataEl = document.getElementById('coffee-quiz-products');
    if (!dataEl) return [];
    
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Coffee Quiz: Failed to parse products:', e);
      return [];
    }
  }

  /**
   * Load saved answers from session storage
   */
  loadAnswers() {
    try {
      const saved = sessionStorage.getItem('coffee_quiz');
      if (saved) {
        const data = JSON.parse(saved);
        return data.answers || {};
      }
    } catch (e) {
      console.error('Failed to load quiz answers:', e);
    }
    return {};
  }

  /**
   * Save answers and current step to session storage
   */
  saveState() {
    try {
      sessionStorage.setItem('coffee_quiz', JSON.stringify({
        currentStep: this.currentScreenIndex,
        answers: this.answers
      }));
    } catch (e) {
      console.error('Failed to save quiz state:', e);
    }
  }

  /**
   * Restore quiz state from session storage
   */
  restoreState() {
    // Restore selected options
    Object.entries(this.answers).forEach(([key, value]) => {
      // Special handling for cups slider
      if (key === 'cupsPerDay') {
        const slider = this.modal.querySelector('[data-cups-slider]');
        const hiddenInput = this.modal.querySelector('[data-quiz-cups-value]');
        if (slider && hiddenInput) {
          // Value is now a number 1-8
          const numValue = parseInt(value) || 1;
          slider.value = numValue;
          hiddenInput.value = numValue;
          // Trigger visual update
          slider.dispatchEvent(new Event('input'));
        }
        return;
      }
      
      const container = this.modal.querySelector(`[data-quiz-options="${key}"]`);
      if (!container) return;
      
      if (Array.isArray(value)) {
        // Multi-select
        value.forEach(v => {
          const input = container.querySelector(`input[value="${v}"]`);
          if (input) {
            input.checked = true;
            input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option')?.classList.add('is-selected');
          }
        });
      } else {
        // Single select
        const input = container.querySelector(`input[value="${value}"]`);
        if (input) {
          input.checked = true;
          input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option')?.classList.add('is-selected');
        }
      }
      
      // Set wasChecked attribute for all inputs in this container
      // This enables re-clicking a pre-selected option to advance
      container.querySelectorAll('input').forEach(input => {
        input.dataset.wasChecked = input.checked ? 'true' : 'false';
      });
    });
    
    // In editor mode, restore the current screen position (if enabled)
    if (window.Shopify && window.Shopify.designMode && this.isEnabledInEditor()) {
      this.restoreEditorPosition();
    }
  }
  
  /**
   * Check if quiz is enabled in theme editor
   */
  isEnabledInEditor() {
    if (!window.Shopify?.designMode) return true;
    if (!this.modal) return false;
    const enableInEditor = this.modal.getAttribute('data-enable-in-editor');
    return enableInEditor !== 'false';
  }
  
  /**
   * Restore screen position in theme editor mode
   */
  restoreEditorPosition() {
    // Skip if disabled in theme editor
    if (!this.isEnabledInEditor()) {
      return;
    }
    
    // First check for preview screen setting (takes priority)
    const previewScreen = this.modal.dataset.previewScreen;
    if (previewScreen && previewScreen !== 'none') {
      const screenIndex = this.screens.indexOf(previewScreen);
      if (screenIndex !== -1) {
        if (!this.modal.open) {
          this.modal.showModal();
          document.body.classList.add('coffee-quiz-open');
        }
        this.goToScreen(screenIndex);
        return;
      }
    }
    
    // Otherwise try to restore saved position
    try {
      const saved = sessionStorage.getItem('coffee_quiz_editor_position');
      if (saved) {
        const savedStep = parseInt(saved);
        if (!isNaN(savedStep) && savedStep >= 0 && savedStep < this.screens.length) {
          if (!this.modal.open) {
            this.modal.showModal();
            document.body.classList.add('coffee-quiz-open');
          }
          this.goToScreen(savedStep);
        }
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
  
  /**
   * Save editor position separately (just the screen index)
   */
  saveEditorPosition() {
    if (window.Shopify && window.Shopify.designMode) {
      try {
        sessionStorage.setItem('coffee_quiz_editor_position', this.currentScreenIndex.toString());
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  /**
   * Bind modal-specific event listeners
   */
  bindModalEvents() {
    // Close button
    this.modal.querySelector('[data-quiz-close]')?.addEventListener('click', () => {
      this.close();
    });

    // Back button
    this.modal.querySelector('[data-quiz-back]')?.addEventListener('click', () => {
      this.goBack();
    });

    // Start button
    this.modal.querySelector('[data-quiz-start]')?.addEventListener('click', () => {
      this.goToScreen(1);
      this.trackEvent('quiz_started', { quiz_name: 'coffee_quiz' });
    });

    // Skip link — move focus (not just scroll) to the main content region.
    // The target <main> is tabindex="-1" so it can receive programmatic focus;
    // relying on the fragment jump alone does not move focus in WebKit/Safari.
    this.modal.querySelector('.coffee-quiz__skip-to-content')?.addEventListener('click', (e) => {
      e.preventDefault();
      const main = this.modal.querySelector('#quiz-main-content');
      main?.focus();
      main?.scrollIntoView();
    });

    // Continue buttons
    this.modal.querySelectorAll('[data-quiz-continue]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.goNext();
      });
    });

    // Option selections
    this.modal.querySelectorAll('[data-quiz-options]').forEach(container => {
      this.bindOptionEvents(container);
    });

    // Size options (results page) — ARIA radio groups
    this.modal.querySelectorAll('[data-subscription-size], [data-onetime-size]').forEach(btn => {
      const selector = btn.hasAttribute('data-subscription-size') ? '[data-subscription-size]' : '[data-onetime-size]';
      btn.addEventListener('click', () => this.handleSizeSelection(btn));
      btn.addEventListener('keydown', (e) => this.handleRadioKeydown(e, btn, selector, (b) => this.handleSizeSelection(b)));
    });

    // Quantity options (results page) — ARIA radio groups
    this.modal.querySelectorAll('[data-subscription-quantity], [data-onetime-quantity]').forEach(btn => {
      const selector = btn.hasAttribute('data-subscription-quantity') ? '[data-subscription-quantity]' : '[data-onetime-quantity]';
      btn.addEventListener('click', () => this.handleQuantitySelection(btn));
      btn.addEventListener('keydown', (e) => this.handleRadioKeydown(e, btn, selector, (b) => this.handleQuantitySelection(b)));
    });

    // Grind selection
    this.modal.querySelectorAll('[data-subscription-grind], [data-onetime-grind]').forEach(select => {
      select.addEventListener('change', () => this.updatePricing());
    });

    // Frequency selection
    this.modal.querySelector('[data-subscription-frequency]')?.addEventListener('change', () => {
      this.updatePricing();
    });

    // Add to cart buttons - prevent any default behavior
    this.modal.querySelector('[data-add-subscription]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('subscription');
    });

    this.modal.querySelector('[data-add-onetime]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('onetime');
    });
    
    // Simple add to cart button (for simplified results page)
    this.modal.querySelector('[data-quiz-add-to-cart]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addToCart('onetime');
    });
    
    // Restart quiz button
    this.modal.querySelector('[data-quiz-restart]')?.addEventListener('click', () => {
      this.restartQuiz();
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.open) {
        this.close();
      }
    });

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });
    
    // Focus trap - keep focus within modal
    this.modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      const focusableElements = this.modal.querySelectorAll(
        'button:not([disabled]):not([hidden]):not([aria-hidden="true"]), ' +
        '[href]:not([hidden]), ' +
        'input:not([disabled]):not([type="hidden"]):not([hidden]), ' +
        'select:not([disabled]):not([hidden]), ' +
        'textarea:not([disabled]):not([hidden]), ' +
        '[tabindex]:not([tabindex="-1"]):not([hidden]):not([aria-hidden="true"])'
      );
      
      const visibleFocusable = Array.from(focusableElements).filter(el => {
        return el.offsetParent !== null && !el.closest('[aria-hidden="true"]') && !el.closest('[hidden]');
      });
      
      if (visibleFocusable.length === 0) return;
      
      const firstFocusable = visibleFocusable[0];
      const lastFocusable = visibleFocusable[visibleFocusable.length - 1];
      
      if (e.shiftKey) {
        // Shift + Tab: go backwards
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: go forwards
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  /**
   * Bind events for option containers (supports list, grid, and horizontal layouts)
   */
  bindOptionEvents(container) {
    const isMultiSelect = container.dataset.multiSelect === 'true';
    const maxSelections = parseInt(container.dataset.maxSelections) || Infinity;
    const autoAdvance = container.dataset.autoAdvance === 'true';
    const optionKey = container.dataset.quizOptions;

    container.querySelectorAll('input').forEach(input => {
      // Auto-advance-on-select has been removed. Selecting an option (by pointer
      // OR keyboard) never changes screens, because native radios fire a
      // synthetic `click` on arrow-key selection too — so there is no reliable
      // way to advance on pointer-only. Every single-select screen now has a
      // Continue button; selecting just enables it. (WCAG 3.2.2 On Input)
      input.addEventListener('change', () => {
        const item = input.closest('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option');
        
        if (isMultiSelect) {
          // Handle multi-select
          if (input.checked) {
            const checkedCount = container.querySelectorAll('input:checked').length;
            if (checkedCount > maxSelections) {
              // Enforce the limit, but announce it — otherwise the checkbox
              // silently reverts with no feedback for AT/low-vision users.
              input.checked = false;
              this.announceToScreenReader(`You can select up to ${maxSelections}. Deselect one first to choose another.`);
              return;
            }
            item?.classList.add('is-selected');
          } else {
            item?.classList.remove('is-selected');
          }
          
          // Update answers array
          const checked = Array.from(container.querySelectorAll('input:checked')).map(i => i.value);
          this.answers[optionKey] = checked;
          
          // Enable/disable continue button
          const continueBtn = this.getCurrentScreen().querySelector('[data-quiz-continue]');
          if (continueBtn) {
            continueBtn.disabled = checked.length === 0;
          }
        } else {
          // Handle single select - support all layout types
          container.querySelectorAll('.coffee-quiz__option-list-item, .coffee-quiz__option-grid-item, .coffee-quiz__option-horizontal-item, .coffee-quiz__scale-option').forEach(i => {
            i.classList.remove('is-selected');
          });
          item?.classList.add('is-selected');
          
          // Mark all inputs in this container to track their checked state for re-click handling
          container.querySelectorAll('input').forEach(i => {
            i.dataset.wasChecked = i.checked ? 'true' : 'false';
          });
          
          this.answers[optionKey] = input.value;
          
          // Track step completion
          this.trackStepCompleted(optionKey, input.value);

          // Enable the Continue button now that a choice exists. Selecting an
          // option never advances the screen (see note above); Continue is the
          // only way forward, for pointer and keyboard alike.
          if (autoAdvance) {
            const continueBtn = this.getCurrentScreen()?.querySelector('[data-quiz-continue]');
            if (continueBtn) continueBtn.disabled = false;
          }
        }
        
        this.saveState();
      });
    });
  }
  
  /**
   * Bind slider events for cups per day
   */
  bindSliderEvents() {
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (!slider) return;
    
    // Initialize slider after a brief delay to ensure layout is complete
    setTimeout(() => this.updateSliderDisplay(slider.value), 50);
    
    // Handle slider input (fires continuously while dragging)
    slider.addEventListener('input', (e) => {
      this.updateSliderDisplay(e.target.value);
    });
    
    // Handle slider change (for accessibility)
    slider.addEventListener('change', (e) => {
      this.updateSliderDisplay(e.target.value);
    });
    
    // Update icon position on window resize
    window.addEventListener('resize', () => {
      if (slider) {
        this.updateSliderDisplay(slider.value);
      }
    });
  }
  
  /**
   * Update the slider display elements
   */
  updateSliderDisplay(value) {
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (!slider) return;
    
    const numValue = parseFloat(value);
    const roundedValue = Math.round(numValue);
    
    // Update hidden input value (store the rounded number)
    const hiddenInput = this.modal.querySelector('[data-quiz-cups-value]');
    if (hiddenInput) {
      hiddenInput.value = roundedValue;
      this.answers.cupsPerDay = roundedValue;
      this.saveState();
    }
    
    // Update visual value display - show "X" in the number span
    const valueDisplay = this.modal.querySelector('[data-cups-value-display]');
    const labelDisplay = this.modal.querySelector('[data-cups-label]');
    if (valueDisplay) {
      valueDisplay.textContent = roundedValue;
    }
    if (labelDisplay) {
      labelDisplay.textContent = roundedValue === 1 ? 'Cup' : 'Cups';
    }
    
    // Update ARIA attributes for screen readers
    const valueText = roundedValue === 1 ? '1 cup per day' : `${roundedValue} cups per day`;
    slider.setAttribute('aria-valuenow', roundedValue);
    slider.setAttribute('aria-valuetext', valueText);
    
    // Position the thumb icon overlay
    const thumbIcon = this.modal.querySelector('[data-slider-thumb-icon]');
    if (thumbIcon) {
      const min = parseFloat(slider.min) || 1;
      const max = parseFloat(slider.max) || 8;
      const percent = (numValue - min) / (max - min);
      // Account for thumb width (60px) - icon should be centered in thumb
      const thumbWidth = 60;
      const iconWidth = 32;
      const trackWidth = slider.offsetWidth;
      // Calculate position: slider percentage of track width, adjusted for thumb size
      const position = percent * (trackWidth - thumbWidth) + (thumbWidth - iconWidth) / 2;
      thumbIcon.style.left = `${position}px`;
    }
    
    // Update cup icons - fill based on current value
    this.modal.querySelectorAll('[data-cup-index]').forEach(cup => {
      const cupIndex = parseInt(cup.dataset.cupIndex);
      cup.classList.toggle('is-filled', cupIndex <= roundedValue);
    });
  }
  
  /**
   * Bind body preference skip link ("I Don't Know")
   */
  bindBodySkipLink() {
    const skipLink = this.modal.querySelector('[data-body-skip]');
    if (!skipLink) return;
    
    skipLink.addEventListener('click', () => {
      // Set the answer to "i-dont-know"
      this.answers.bodyPreference = 'i-dont-know';
      this.saveState();
      
      // Track step completion
      this.trackStepCompleted('bodyPreference', 'i-dont-know');
      
      // Advance to next screen
      this.goNext();
    });
  }

  /**
   * Bind email capture form events
   */
  bindEmailForm() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    const skipBtn = this.modal.querySelector('[data-quiz-email-skip]');
    
    if (!form) return;
    
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    const emailInput = form.querySelector('input[type="email"]');
    
    // Store original button text for reset
    if (submitBtn) {
      submitBtn.setAttribute('data-original-text', submitBtn.textContent.trim());
      // Start with button disabled
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-disabled', 'true');
    }
    
    // Real-time email validation for button state
    if (emailInput) {
      emailInput.addEventListener('input', () => {
        this.updateEmailSubmitButton();
      });
      
      // Also validate on blur for better UX
      emailInput.addEventListener('blur', () => {
        const email = emailInput.value.trim();
        const emailError = form.querySelector('#quiz-email-error');
        
        if (email && !this.isValidEmail(email)) {
          if (emailError) emailError.textContent = 'Please enter a valid email address';
          emailInput.classList.add('is-error');
          emailInput.setAttribute('aria-invalid', 'true');
        } else {
          if (emailError) emailError.textContent = '';
          emailInput.classList.remove('is-error');
          emailInput.setAttribute('aria-invalid', 'false');
        }
      });
    }
    
    // Handle form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const emailInput = form.querySelector('input[type="email"]');
      const consentCheckbox = form.querySelector('input[name="email_consent"]');
      const submitBtn = form.querySelector('[data-quiz-email-submit]');
      // Use ID selectors for reliability
      const emailError = form.querySelector('#quiz-email-error');
      const consentError = form.querySelector('#quiz-consent-error');
      
      // Clear previous errors
      if (emailError) emailError.textContent = '';
      if (consentError) consentError.textContent = '';
      emailInput.classList.remove('is-error');
      emailInput.setAttribute('aria-invalid', 'false');
      consentCheckbox?.parentElement?.classList.remove('is-error');
      
      // Validate email
      const email = emailInput.value.trim();
      if (!email) {
        if (emailError) emailError.textContent = 'Please enter your email address';
        emailInput.classList.add('is-error');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return;
      }
      
      if (!this.isValidEmail(email)) {
        if (emailError) emailError.textContent = 'Please enter a valid email address';
        emailInput.classList.add('is-error');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return;
      }
      
      // Validate consent
      if (!consentCheckbox.checked) {
        if (consentError) consentError.textContent = 'Please agree to receive marketing emails';
        consentCheckbox.parentElement.classList.add('is-error');
        consentCheckbox.focus();
        return;
      }
      
      // Show loading state
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = 'Submitting...';
      
      try {
        // Subscribe to Klaviyo
        await this.subscribeToKlaviyo(email);
        
        // Mark email as submitted so we skip this screen on return
        this.markEmailAsSubmitted();
        this.emailSubmitted = true;
        
        // Track the email capture
        this.trackEvent('quiz_email_captured', { 
          quiz_name: 'coffee_quiz',
          email_captured: true 
        });
        
        // Move to results
        this.goNext();
      } catch (error) {
        console.error('Failed to subscribe to Klaviyo:', error);
        if (emailError) emailError.textContent = 'Something went wrong. Please try again.';
        submitBtn.disabled = false;
        submitBtn.setAttribute('aria-disabled', 'false');
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = 'See My Results';
        // Return focus to the re-enabled button; disabling it dropped focus to
        // <body>, and the error is announced via the #quiz-email-error alert.
        submitBtn.focus();
      }
    });
    
    // Handle skip button
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        // Mark as submitted so we skip this screen on return
        this.markEmailAsSubmitted();
        this.emailSubmitted = true;
        
        this.trackEvent('quiz_email_skipped', { quiz_name: 'coffee_quiz' });
        this.goNext();
      });
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Update email submit button state based on input validity
   */
  updateEmailSubmitButton() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    if (!form) return;
    
    const emailInput = form.querySelector('input[type="email"]');
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    
    if (!emailInput || !submitBtn) return;
    
    const email = emailInput.value.trim();
    const isValid = this.isValidEmail(email);
    
    submitBtn.disabled = !isValid;
    submitBtn.setAttribute('aria-disabled', !isValid ? 'true' : 'false');
  }

  /**
   * Check if email has already been submitted this session
   */
  hasEmailBeenSubmitted() {
    try {
      return sessionStorage.getItem('coffee_quiz_email_submitted') === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark email as submitted
   */
  markEmailAsSubmitted() {
    try {
      sessionStorage.setItem('coffee_quiz_email_submitted', 'true');
    } catch (e) {
      console.error('Failed to save email submitted state:', e);
    }
  }

  /**
   * Reset email form to initial state
   */
  resetEmailForm() {
    const form = this.modal.querySelector('[data-quiz-email-form]');
    if (!form) return;
    
    const submitBtn = form.querySelector('[data-quiz-email-submit]');
    const emailInput = form.querySelector('input[type="email"]');
    // Use ID selectors for reliability
    const emailError = form.querySelector('#quiz-email-error');
    const consentError = form.querySelector('#quiz-consent-error');
    
    // Reset button state - disabled until valid email
    if (submitBtn) {
      submitBtn.textContent = submitBtn.getAttribute('data-original-text') || 'See My Results';
      submitBtn.removeAttribute('aria-busy');
      // Check current email value to determine button state
      const hasValidEmail = emailInput && this.isValidEmail(emailInput.value.trim());
      submitBtn.disabled = !hasValidEmail;
      submitBtn.setAttribute('aria-disabled', !hasValidEmail ? 'true' : 'false');
    }
    
    // Clear errors
    if (emailError) emailError.textContent = '';
    if (consentError) consentError.textContent = '';
    if (emailInput) {
      emailInput.classList.remove('is-error');
      emailInput.setAttribute('aria-invalid', 'false');
    }
  }

  /**
   * Subscribe email to Klaviyo
   */
  async subscribeToKlaviyo(email) {
    // Use Klaviyo's identify method to add the email
    if (typeof window.klaviyo !== 'undefined') {
      // Build detailed profile properties
      const profileData = {
        '$email': email,
        '$consent': ['email'],
        // Quiz completion flag
        'Coffee Quiz Completed': true,
        'Coffee Quiz Completed Date': new Date().toISOString(),
        // Individual quiz answers for segmentation
        'Quiz - Coffee Experience': this.answers.experience || '',
        'Quiz - Brew Method': this.answers.brewMethod || '',
        'Quiz - Flavor Preferences': Array.isArray(this.answers.flavors) ? this.answers.flavors.join(', ') : (this.answers.flavors || ''),
        'Quiz - How They Take It': this.answers.howYouTakeIt || '',
        'Quiz - Body Preference': this.answers.bodyPreference || '',
        'Quiz - Coffee Is My': this.answers.coffeeRitual || '',
        'Quiz - Cups Per Day': this.answers.cupsPerDay || '',
        // Full answers as JSON for reference
        'Quiz Answers JSON': JSON.stringify(this.answers),
        // Recommendation
        'Quiz Recommended Product': this.recommendedProduct?.title || '',
        'Quiz Recommended Product Handle': this.recommendedProduct?.handle || '',
        'Quiz Recommended Product URL': this.recommendedProduct?.url || ''
      };
      
      // Identify the user with their email and quiz data
      window.klaviyo.identify(profileData);
      
      // Also track as an event for flow triggers
      window.klaviyo.push(['track', 'Coffee Quiz Completed', {
        'Experience Level': this.answers.experience || '',
        'Brew Method': this.answers.brewMethod || '',
        'Flavor Preferences': Array.isArray(this.answers.flavors) ? this.answers.flavors : [this.answers.flavors].filter(Boolean),
        'How They Take It': this.answers.howYouTakeIt || '',
        'Body Preference': this.answers.bodyPreference || '',
        'Coffee Is My': this.answers.coffeeRitual || '',
        'Cups Per Day': this.answers.cupsPerDay || '',
        'Recommended Product': this.recommendedProduct?.title || '',
        'Recommended Product Handle': this.recommendedProduct?.handle || '',
        'Recommended Product URL': this.recommendedProduct?.url || '',
        'Recommended Product Price': this.recommendedProduct?.price || ''
      }]);
      
      // If a list ID is provided, subscribe to that specific list
      if (this.klaviyoListId) {
        window.klaviyo.push(['subscribe', this.klaviyoListId, { '$email': email }]);
      }
      return true;
    }
    return false;
  }



  /**
   * Handle size button selection
   */
  handleSizeSelection(btn) {
    const isSubscription = btn.hasAttribute('data-subscription-size');
    const selector = isSubscription ? '[data-subscription-size]' : '[data-onetime-size]';
    this.selectRadioInGroup(btn, selector);
    this.updatePricing();
  }

  /**
   * Handle quantity button selection
   */
  handleQuantitySelection(btn) {
    const isSubscription = btn.hasAttribute('data-subscription-quantity');
    const selector = isSubscription ? '[data-subscription-quantity]' : '[data-onetime-quantity]';
    this.selectRadioInGroup(btn, selector);
    this.updatePricing();
  }

  /**
   * Apply single-select radio semantics to a group of role="radio" buttons:
   * check the chosen one and roving-tabindex the group so it is a single tab
   * stop, per the ARIA radio pattern. (WCAG 1.3.1 / 4.1.2)
   * @param {HTMLElement} btn - the newly selected button
   * @param {string} selector - selector matching all buttons in the group
   */
  selectRadioInGroup(btn, selector) {
    this.modal.querySelectorAll(selector).forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
      b.tabIndex = -1;
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');
    btn.tabIndex = 0;
  }

  /**
   * Arrow-key navigation for the results-page radio groups. Left/Up selects the
   * previous option, Right/Down the next (wrapping); selection follows focus.
   * @param {KeyboardEvent} e
   * @param {HTMLElement} btn - the button currently focused
   * @param {string} selector - selector matching all buttons in the group
   * @param {(b: HTMLElement) => void} select - selection handler for the group
   */
  handleRadioKeydown(e, btn, selector, select) {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    // Skip sizes this coffee isn't sold in, so arrow keys can't land on them.
    const group = Array.from(this.modal.querySelectorAll(selector)).filter(b => !b.disabled);
    if (group.length === 0) return;
    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const next = group[(group.indexOf(btn) + (forward ? 1 : -1) + group.length) % group.length];
    if (next) {
      select(next);
      next.focus();
    }
  }

  /**
   * Open the quiz modal
   */
  open() {
    if (window.Shopify?.designMode && !this.isEnabledInEditor()) {
      return;
    }

    // Try to find modal if not already found
    if (!this.modal) {
      this.modal = document.getElementById('coffee-quiz-modal');
      if (this.modal) {
        // Initialize modal events now that we found it
        this.products = this.loadProducts();
        this.bindModalEvents();
        this.updateProgress();
        this.restoreState();
      }
    }
    
    if (!this.modal) {
      console.error('Coffee Quiz: Modal element not found');
      return;
    }
    
    try {
      // Store the element that triggered the modal for focus return
      this.previousFocus = document.activeElement;
      
      this.modal.showModal();
      document.body.classList.add('coffee-quiz-open');
      
      // Set focus to the first focusable element or the start button
      const startBtn = this.modal.querySelector('[data-quiz-start]');
      if (startBtn) {
        startBtn.focus();
      }
      
      // Announce to screen readers
      this.announceToScreenReader('Coffee quiz opened. ' + (this.modal.querySelector('#quiz-title')?.textContent || 'Find your perfect coffee'));
      
      this.trackEvent('quiz_modal_opened', { quiz_name: 'coffee_quiz' });
    } catch (e) {
      console.error('Coffee Quiz: Error opening modal', e);
    }
  }

  /**
   * Close the quiz modal
   */
  close() {
    this.modal.close();
    document.body.classList.remove('coffee-quiz-open');
    
    // Return focus to the element that opened the modal
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
    
    this.announceToScreenReader('Coffee quiz closed');
  }
  
  /**
   * Announce message to screen readers via live region
   */
  announceToScreenReader(message) {
    const liveRegion = document.getElementById('quiz-live-region');
    if (liveRegion) {
      liveRegion.textContent = '';
      // Small delay to ensure the change is announced
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 100);
    }
  }

  /**
   * Get current screen element
   */
  getCurrentScreen() {
    return this.modal.querySelector(`[data-quiz-screen="${this.screens[this.currentScreenIndex]}"]`);
  }

  /**
   * Navigate to a specific screen
   * @param {number} index - The screen index to navigate to
   * @param {boolean} isGoingBack - Whether we're navigating backward (optional)
   */
  goToScreen(index, isGoingBack = false) {
    if (index < 0 || index >= this.screens.length) return;
    
    // Skip email capture screen if email was already submitted (only when going forward)
    if (this.screens[index] === 'email-capture' && this.emailSubmitted && !isGoingBack) {
      // Go directly to results instead
      index = this.screens.indexOf('results');
      if (index === -1) index = this.screens.length - 1;
    }
    
    // Reset email form state when showing email capture screen
    if (this.screens[index] === 'email-capture') {
      this.resetEmailForm();
    }
    
    // Hide all screens
    this.modal.querySelectorAll('[data-quiz-screen]').forEach(screen => {
      screen.classList.remove('is-active');
      screen.setAttribute('aria-hidden', 'true');
    });
    
    // Show target screen
    this.currentScreenIndex = index;
    const targetScreen = this.modal.querySelector(`[data-quiz-screen="${this.screens[index]}"]`);
    if (targetScreen) {
      targetScreen.classList.add('is-active');
      targetScreen.setAttribute('aria-hidden', 'false');
    }
    
    // Update back button visibility and accessibility
    const backBtn = this.modal.querySelector('[data-quiz-back]');
    if (backBtn) {
      backBtn.hidden = index === 0;
      backBtn.setAttribute('aria-hidden', index === 0 ? 'true' : 'false');
      backBtn.tabIndex = index === 0 ? -1 : 0;
    }
    
    // Update progress bar
    this.updateProgress();
    
    // Save state
    this.saveState();
    this.saveEditorPosition();
    
    // Calculate recommendation when reaching email capture or results screen
    // Always recalculate to account for any answer changes if user went back
    if (this.screens[index] === 'email-capture' || this.screens[index] === 'results') {
      this.calculateRecommendation();
    }
    
    // If results screen, track completion
    if (this.screens[index] === 'results') {
      this.trackEvent('quiz_completed', {
        quiz_name: 'coffee_quiz',
        answers: this.answers
      });
    }
    
    // Scroll to top of modal content
    this.modal.querySelector('.coffee-quiz__content')?.scrollTo(0, 0);
    
    // Sync the Continue button's enabled state to whether this screen already
    // has a selection — covers multi-select screens (e.g. flavors) and the
    // single-select auto-advance screens when the user navigates Back to them.
    const selectableContainer = targetScreen?.querySelector('[data-multi-select="true"], [data-auto-advance="true"]');
    if (selectableContainer) {
      const checkedInputs = selectableContainer.querySelectorAll('input:checked');
      const continueBtn = targetScreen?.querySelector('[data-quiz-continue]');
      if (continueBtn) {
        continueBtn.disabled = checkedInputs.length === 0;
      }
    }
    
    // Announce screen change to screen readers and manage focus
    const screenTitle = targetScreen?.querySelector('h1, h2')?.textContent;
    if (screenTitle) {
      this.announceToScreenReader(`Step ${index + 1} of ${this.screens.length}: ${screenTitle}`);
    }
    
    // Move focus to the screen heading or first interactive element
    setTimeout(() => {
      const heading = targetScreen?.querySelector('h1, h2');
      const firstFocusable = targetScreen?.querySelector('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
      
      if (heading) {
        // Make heading focusable temporarily
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      } else if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  /**
   * Go to next screen
   */
  goNext() {
    // Track step completion for multi-select screens
    const currentScreen = this.screens[this.currentScreenIndex];
    if (currentScreen === 'flavors' && this.answers.flavors) {
      this.trackStepCompleted('flavors', this.answers.flavors.join(', '));
    }
    
    this.goToScreen(this.currentScreenIndex + 1);
  }

  /**
   * Go back to previous screen
   */
  goBack() {
    let targetIndex = this.currentScreenIndex - 1;
    
    // When going back, skip the email-capture screen if email was already submitted
    // (go to the screen before email-capture instead)
    if (this.screens[targetIndex] === 'email-capture' && this.emailSubmitted) {
      targetIndex = targetIndex - 1;
    }
    
    if (targetIndex >= 0) {
      this.goToScreen(targetIndex, true); // Pass true to indicate going back
    }
  }

  /**
   * Restart the quiz from the beginning
   */
  restartQuiz() {
    // Clear answers
    this.answers = {};
    this.recommendedProduct = null;
    this.emailSubmitted = false;
    
    // Clear session storage
    sessionStorage.removeItem('coffee_quiz');
    sessionStorage.removeItem('coffee_quiz_email_submitted');
    
    // Reset UI elements
    this.modal.querySelectorAll('input[type="radio"]').forEach(input => {
      input.checked = false;
    });
    this.modal.querySelectorAll('input[type="checkbox"]').forEach(input => {
      input.checked = false;
    });
    this.modal.querySelectorAll('.is-selected').forEach(el => {
      el.classList.remove('is-selected');
    });
    
    // Reset slider
    const slider = this.modal.querySelector('[data-cups-slider]');
    if (slider) {
      slider.value = 1;
      this.updateSliderDisplay(1);
    }
    
    // Reset results display
    const loadingEl = this.modal.querySelector('.coffee-quiz__product-loading');
    const productCard = this.modal.querySelector('.coffee-quiz__product-card');
    const purchaseOptions = this.modal.querySelector('.coffee-quiz__purchase-options');
    const resultsFooter = this.modal.querySelector('.coffee-quiz__results-footer');
    if (loadingEl) loadingEl.style.display = 'block';
    if (productCard) {
      productCard.style.display = 'none';
      productCard.classList.remove('animate-in');
    }
    if (purchaseOptions) {
      purchaseOptions.style.display = 'none';
      purchaseOptions.classList.remove('animate-in');
    }
    if (resultsFooter) {
      resultsFooter.classList.remove('animate-in');
    }
    
    // Reset size and quantity selections back to the first option in each group
    // (12oz / 1), keeping the ARIA radio state and roving tabindex in step.
    [
      '[data-subscription-size]',
      '[data-onetime-size]',
      '[data-subscription-quantity]',
      '[data-onetime-quantity]'
    ].forEach(selector => {
      const first = this.modal.querySelector(selector);
      if (first) this.selectRadioInGroup(first, selector);
    });

    // Re-enable every size; availability is recomputed for the next recommendation.
    this.modal.querySelectorAll('.coffee-quiz__size-option').forEach(btn => {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
    });
    this.sizeProducts = {};
    
    // Go back to intro
    this.goToScreen(0);
    
    // Track restart
    this.trackEvent('quiz_restarted', { quiz_name: 'coffee_quiz' });
  }

  /**
   * Update progress bar
   */
  updateProgress() {
    const progressBar = this.modal.querySelector('[data-quiz-progress]');
    const progressContainer = this.modal.querySelector('[data-quiz-progress-container]');
    if (!progressBar) return;
    
    // Don't count intro and results in progress
    const totalSteps = this.screens.length - 2; // Exclude intro and results
    const currentStep = Math.max(0, this.currentScreenIndex - 1);
    const progress = this.currentScreenIndex === 0 ? 0 : 
                     this.currentScreenIndex >= this.screens.length - 1 ? 100 :
                     (currentStep / totalSteps) * 100;
    
    progressBar.style.width = `${progress}%`;
    
    // Update ARIA attributes for screen readers
    if (progressContainer) {
      progressContainer.setAttribute('aria-valuenow', Math.round(progress));
      progressContainer.setAttribute('aria-valuetext', `Quiz progress: ${Math.round(progress)}% complete`);
    }
  }

  /**
   * Calculate and display product recommendation
   */
  calculateRecommendation() {
    if (this.products.length === 0) {
      return;
    }

    // Exclude sampler products and out-of-stock products from recommendations
    const excludedHandles = ['house-blend-coffee-sampler', 'coffee-sampler', 'sampler'];
    const baseEligible = this.products.filter(product => {
      // Exclude out-of-stock products
      if (!product.available) return false;

      // Exclude the bigger-bag products. They are separate products sharing a
      // base handle (see QUIZ_SIZE_HANDLE_SUFFIX), so recommending one would
      // duplicate its 12oz sibling and break the size selector's handle math.
      if (/-(?:2lb|5lb)$/.test(product.handle.toLowerCase())) return false;

      // Exclude sampler products
      const handleLower = product.handle.toLowerCase();
      const titleLower = product.title.toLowerCase();
      const isSampler = excludedHandles.some(exc => handleLower.includes(exc)) ||
                        titleLower.includes('sampler');
      if (isSampler) return false;

      // Exclude limited-time / non-subscription coffees unless the feature is turned on.
      // These can't be subscribed to, and the results page pushes the subscription model.
      if (!this.includeLimitedTime && this.isLimitedTimeProduct(product)) return false;

      return true;
    });

    // Caffeine preference is a hard filter (a decaf seeker should never get a caffeinated coffee).
    // Fall back to the unfiltered set if nothing matches, so the quiz always returns something.
    const caffeineMatched = baseEligible.filter(product => this.matchesCaffeinePreference(product));
    const eligibleProducts = caffeineMatched.length > 0 ? caffeineMatched : baseEligible;

    // Score each product
    const scoredProducts = eligibleProducts.map(product => {
      let score = 0;
      const matchedTags = [];
      const scoreBreakdown = []; // Track how score is built
      const debugInfo = {
        title: product.title,
        quizFlavors: product.quizFlavors,
        quizBody: product.quizBody,
        quizBrewMethods: product.quizBrewMethods
      };
      
      // Flat 1-point scoring: every matching answer is worth exactly 1 point.
      // Caffeine preference and limited-time eligibility are handled as hard filters
      // upstream, so they don't contribute points here.

      // Caffeine: surface a tag for the matched preference (no points — already filtered).
      const caffeineTag = this.getCaffeineTag();
      if (caffeineTag) matchedTags.push(caffeineTag);

      // Experience: an advanced drinker scores +1 on limited-time / rare coffees.
      // Only relevant when limited-time coffees are included in the results.
      if (this.answers.experience === 'advanced' && this.isLimitedTimeProduct(product)) {
        score += 1;
        matchedTags.push('Rare & Exclusive');
        scoreBreakdown.push('Experience: +1 (advanced + limited-time coffee)');
      }

      // Flavors: +1 for each selected flavor note this coffee actually has.
      const productFlavors = this.normalizeMetafieldList(product.quizFlavors);

      scoreBreakdown.push(`Flavor metafield raw: ${JSON.stringify(product.quizFlavors)}`);
      scoreBreakdown.push(`Flavor metafield normalized: [${productFlavors.join(', ')}]`);

      if (this.answers.flavors && productFlavors.length > 0) {
        this.answers.flavors.forEach(flavor => {
          const flavorKey = flavor.replace(/-/g, ' ').toLowerCase();
          const hasFlavor = productFlavors.some(pf => pf.includes(flavorKey) || flavorKey.includes(pf));

          if (hasFlavor) {
            score += 1;
            matchedTags.push(this.capitalizeFirst(flavor.replace(/-/g, ' ')));
            scoreBreakdown.push(`Flavor: +1 (matched "${flavor}")`);
          }
        });
      } else if (this.answers.flavors && productFlavors.length === 0) {
        scoreBreakdown.push('Flavor: no metafield data on product');
      }

      // Body: +1 if the coffee's body matches the customer's preference.
      const productBody = this.normalizeMetafieldValue(product.quizBody);

      scoreBreakdown.push(`Body metafield raw: ${JSON.stringify(product.quizBody)}`);
      scoreBreakdown.push(`Body metafield normalized: "${productBody}"`);

      if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know' && productBody) {
        const userBodyPref = this.answers.bodyPreference.replace('-body', '').toLowerCase();

        if (productBody === userBodyPref || productBody.includes(userBodyPref)) {
          score += 1;
          matchedTags.push(this.capitalizeFirst(this.answers.bodyPreference.replace(/-/g, ' ')));
          scoreBreakdown.push(`Body: +1 (matched "${userBodyPref}" with "${productBody}")`);
        } else {
          scoreBreakdown.push(`Body: no match ("${userBodyPref}" vs "${productBody}")`);
        }
      } else if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know' && !productBody) {
        scoreBreakdown.push('Body: no metafield data on product');
      }

      // Brew method: +1 if the coffee supports the customer's brew method.
      const productBrewMethods = this.normalizeMetafieldList(product.quizBrewMethods);

      scoreBreakdown.push(`Brew metafield raw: ${JSON.stringify(product.quizBrewMethods)}`);
      scoreBreakdown.push(`Brew metafield normalized: [${productBrewMethods.join(', ')}]`);

      if (this.answers.brewMethod && productBrewMethods.length > 0) {
        const userBrewMethod = this.answers.brewMethod.toLowerCase().replace(/-/g, ' ');

        const hasBrewMatch = productBrewMethods.some(bm =>
          bm.includes(userBrewMethod) || userBrewMethod.includes(bm)
        );

        if (hasBrewMatch) {
          score += 1;
          matchedTags.push(this.capitalizeFirst(this.answers.brewMethod.replace(/-/g, ' ')));
          scoreBreakdown.push(`Brew: +1 (matched "${userBrewMethod}" with product brew methods)`);
        } else {
          scoreBreakdown.push(`Brew: no match ("${userBrewMethod}" vs [${productBrewMethods.join(', ')}])`);
        }
      } else if (this.answers.brewMethod && productBrewMethods.length === 0) {
        scoreBreakdown.push('Brew: no metafield data on product');
      }

      return { product, score, matchedTags, debugInfo, scoreBreakdown };
    });
    
    // Sort by score and get the best match
    scoredProducts.sort((a, b) => b.score - a.score);

    const best = scoredProducts[0];

    if (!best) {
      return;
    }

    this.recommendedProduct = best.product;
    this.displayRecommendation(best.product, best.matchedTags);
  }

  /**
   * A limited-time / rare coffee is one with no subscription selling plans.
   * These are gated behind the "include limited-time coffees" setting and, when shown,
   * are sold as a one-time purchase only (no subscription option).
   * @param {Object} product
   * @returns {boolean}
   */
  isLimitedTimeProduct(product) {
    return !product.sellingPlans || product.sellingPlans.length === 0;
  }

  /**
   * Whether a product is decaf based on its tags.
   * @param {Object} product
   * @returns {boolean}
   */
  isDecafProduct(product) {
    return product.tags.some(t => t.toLowerCase().includes('decaf'));
  }

  /**
   * Whether a product is half-caff based on handle, title, or tags.
   * @param {Object} product
   * @returns {boolean}
   */
  isHalfCaffProduct(product) {
    return product.handle.toLowerCase().includes('miti') ||
           product.title.toLowerCase().includes('half caff') ||
           product.tags.some(t => t.toLowerCase().includes('half caff') || t.toLowerCase().includes('half-caff'));
  }

  /**
   * Hard filter for the caffeine question. A product is eligible only if it matches
   * the customer's caffeine preference (decaf / half-caff / caffeinated).
   * @param {Object} product
   * @returns {boolean}
   */
  matchesCaffeinePreference(product) {
    const preference = this.answers.caffeinePreference;
    if (!preference) return true;

    const isDecaf = this.isDecafProduct(product);
    const isHalfCaff = this.isHalfCaffProduct(product);

    if (preference === 'decaf') return isDecaf;
    if (preference === 'half-caff') return isHalfCaff;
    if (preference === 'caffeinated') return !isDecaf && !isHalfCaff;
    return true;
  }

  /**
   * Display tag for the matched caffeine preference (cosmetic only — no scoring).
   * @returns {string} Tag label, or '' if no caffeine preference was set
   */
  getCaffeineTag() {
    const preference = this.answers.caffeinePreference;
    if (preference === 'decaf') return 'Decaf';
    if (preference === 'half-caff') return 'Half Caff';
    if (preference === 'caffeinated') return 'Caffeinated';
    return '';
  }

  /**
   * Normalize metafield value to a lowercase string
   * Handles: string, array, object, null/undefined
   */
  normalizeMetafieldValue(value) {
    if (!value) return '';
    
    // If it's an array, join and lowercase
    if (Array.isArray(value)) {
      return value.map(v => String(v).toLowerCase().trim()).join(', ');
    }
    
    // If it's an object, try to get a value property or stringify
    if (typeof value === 'object') {
      return String(value.value || value.name || JSON.stringify(value)).toLowerCase().trim();
    }
    
    // Otherwise treat as string
    return String(value).toLowerCase().trim();
  }

  /**
   * Normalize metafield to an array of lowercase strings
   * Handles: string (comma-separated), array, null/undefined
   */
  normalizeMetafieldList(value) {
    if (!value) return [];
    
    // If it's already an array, normalize each item
    if (Array.isArray(value)) {
      return value.map(v => String(v).toLowerCase().trim()).filter(v => v);
    }
    
    // If it's an object, try to extract array or value
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Handle Shopify metafield object format
      const actualValue = value.value || value;
      if (Array.isArray(actualValue)) {
        return actualValue.map(v => String(v).toLowerCase().trim()).filter(v => v);
      }
      return [String(actualValue).toLowerCase().trim()].filter(v => v);
    }
    
    // Otherwise treat as comma-separated string
    return String(value).toLowerCase().split(',').map(v => v.trim()).filter(v => v);
  }

  /**
   * Get user's quiz selections as display tags
   */
  getUserSelectionTags() {
    const tags = [];
    
    // Add brew method
    if (this.answers.brewMethod) {
      tags.push(this.capitalizeFirst(this.answers.brewMethod.replace(/-/g, ' ')));
    }
    
    // Add flavors
    if (this.answers.flavors && Array.isArray(this.answers.flavors)) {
      this.answers.flavors.forEach(flavor => {
        tags.push(this.capitalizeFirst(flavor.replace(/-/g, ' ')));
      });
    }
    
    // Add body preference (if not "don't know")
    if (this.answers.bodyPreference && this.answers.bodyPreference !== 'i-dont-know') {
      tags.push(this.capitalizeFirst(this.answers.bodyPreference.replace(/-/g, ' ')));
    }
    
    // Add caffeine preference
    if (this.answers.caffeinePreference) {
      tags.push(this.capitalizeFirst(this.answers.caffeinePreference.replace(/-/g, ' ')));
    }
    
    return tags;
  }

  /**
   * Capitalize first letter of each word
   */
  capitalizeFirst(str) {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  /**
   * Extract intro description (everything before "Coffee Description")
   */
  extractNotes(description) {
    if (!description) return '';
    
    // Strip HTML and normalize whitespace
    const cleanDesc = description
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<\/div>/gi, ' ')
      .replace(/<\/span>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&#?\w+;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Extract everything before "Coffee Description"
    const coffeeDescIndex = cleanDesc.toLowerCase().indexOf('coffee description');
    if (coffeeDescIndex > 0) {
      return cleanDesc.substring(0, coffeeDescIndex).trim();
    }
    
    // Fallback: first paragraph
    const firstParagraph = cleanDesc.split(/(?:\.\s+[A-Z]|\n)/)[0];
    if (firstParagraph?.length > 20) {
      return firstParagraph.trim() + (firstParagraph.endsWith('.') ? '' : '.');
    }
    
    // Last resort: truncate
    return cleanDesc.length > 150 ? cleanDesc.substring(0, 150).trim() + '...' : cleanDesc;
  }
  
  /**
   * Display the recommended product
   */
  displayRecommendation(product, matchedTags) {
    // Show product card and purchase options, hide loading
    const loadingEl = this.modal.querySelector('.coffee-quiz__product-loading');
    const productCard = this.modal.querySelector('.coffee-quiz__product-card');
    const purchaseOptions = this.modal.querySelector('.coffee-quiz__purchase-options');
    const resultsFooter = this.modal.querySelector('.coffee-quiz__results-footer');
    
    if (loadingEl) loadingEl.style.display = 'none';
    
    // Show elements with animation classes
    if (productCard) {
      productCard.style.display = 'block';
      productCard.classList.add('animate-in');
    }
    if (purchaseOptions) {
      purchaseOptions.style.display = 'grid';
      purchaseOptions.classList.add('animate-in');
    }
    if (resultsFooter) {
      resultsFooter.classList.add('animate-in');
    }

    // Limited-time coffees can't be subscribed to — hide the subscription option and
    // present the one-time purchase as the recommended (and only) choice.
    const isLimited = this.isLimitedTimeProduct(product);
    const subscriptionOption = this.modal.querySelector('[data-purchase-type="subscription"]');
    const oneTimeOption = this.modal.querySelector('[data-purchase-type="onetime"]');
    if (subscriptionOption) {
      subscriptionOption.hidden = isLimited;
    }
    if (oneTimeOption) {
      // When subscriptions are unavailable, the one-time card becomes the recommended option.
      oneTimeOption.classList.toggle('is-recommended', isLimited);
    }
    if (purchaseOptions) {
      purchaseOptions.classList.toggle('coffee-quiz__purchase-options--single', isLimited);
    }

    // Update the usage note (inside subscription option)
    this.updateUsageNote();
    
    // Update product info
    const imageEl = this.modal.querySelector('[data-quiz-product-image]');
    const nameEl = this.modal.querySelector('[data-quiz-product-name]');
    const notesEl = this.modal.querySelector('[data-quiz-product-notes]');
    const tagsContainer = this.modal.querySelector('[data-quiz-matched-tags]');
    const ratingContainer = this.modal.querySelector('[data-quiz-product-rating]');
    const reviewsWidgetContainer = this.modal.querySelector('[data-quiz-product-reviews-widget]');
    
    if (imageEl) {
      imageEl.src = product.image;
      imageEl.alt = product.title;
      
      // Wrap image in link to PDP (opens in new window)
      const imageLink = this.modal.querySelector('[data-quiz-product-image-link]');
      if (imageLink) {
        imageLink.href = product.url;
        imageLink.target = '_blank';
        imageLink.rel = 'noopener';
        // The image alt (product title) now names the link; drop the static
        // fallback aria-label so it doesn't override that name.
        imageLink.removeAttribute('aria-label');
      }
    }
    
    if (nameEl) {
      nameEl.textContent = product.title;
    }
    
    // Display quiz description from metafield (or fallback to extractNotes)
    if (notesEl) {
      notesEl.textContent = product.quizDescription || this.extractNotes(product.description);
    }
    
    // Set "More Info" link URL
    const moreInfoLink = this.modal.querySelector('[data-quiz-product-link]');
    if (moreInfoLink) {
      moreInfoLink.href = product.url;
    }
    
    // Render the same Klaviyo reviews component used in product contexts
    if (ratingContainer && reviewsWidgetContainer && product.id) {
      ratingContainer.hidden = false;
      reviewsWidgetContainer.textContent = '';

      const reviewsWidget = document.createElement('div');
      reviewsWidget.className = 'klaviyo-star-rating-widget';
      reviewsWidget.setAttribute('data-id', String(product.id));
      reviewsWidget.setAttribute('data-product-title', product.title || '');
      reviewsWidget.setAttribute('data-product-type', product.type || '');
      reviewsWidgetContainer.appendChild(reviewsWidget);
    } else if (ratingContainer) {
      ratingContainer.hidden = true;
    }
    
    // Display user selections as tags
    if (tagsContainer) {
      const userSelections = this.getUserSelectionTags();
      tagsContainer.innerHTML = userSelections.map(tag => 
        `<span class="coffee-quiz__matched-tag">${tag}</span>`
      ).join('');
    }
    
    // Default grind to Whole Bean
    this.modal.querySelectorAll('[data-subscription-grind], [data-onetime-grind]').forEach(select => {
      select.value = 'whole-bean';
    });
    
    // Resolve the product behind each size button, then pre-select and price.
    this.initSizeOptions();
    
    // Track recommendation
    this.trackEvent('quiz_product_recommended', {
      product_id: product.id,
      product_title: product.title,
      matched_tags: matchedTags
    });
  }

  /**
   * Base (12oz) handle for a coffee, stripping any size suffix so a size
   * sibling that slipped into the collection still resolves to the right family.
   * @param {Object} product
   * @returns {string}
   */
  baseHandle(product) {
    return String(product?.handle || '').replace(/-(?:2lb|5lb)$/, '');
  }

  /**
   * Fetch and cache a size sibling product. Resolves to null when there is no
   * such product (a coffee sold only in 12oz), so callers can disable the size.
   * @param {string} handle
   * @returns {Promise<Object|null>}
   */
  async fetchSizeProduct(handle) {
    if (this.sizeProductCache.has(handle)) {
      return this.sizeProductCache.get(handle);
    }

    let normalized = null;
    try {
      const response = await fetch(`/products/${handle}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        normalized = {
          id: data.id,
          title: data.title,
          handle: data.handle,
          url: `/products/${data.handle}`,
          price: data.price,
          available: data.available,
          variants: (data.variants || []).map(v => ({
            id: v.id,
            title: v.title,
            price: v.price,
            available: v.available,
            options: v.options || []
          })),
          // Each size product carries its own selling plans, so a subscription
          // must use the plan belonging to the size actually being bought.
          sellingPlans: (data.selling_plan_groups || []).flatMap(group =>
            (group.selling_plans || []).map(plan => ({ id: plan.id, name: plan.name }))
          )
        };
      }
    } catch (e) {
      // A 404 or network failure is treated the same as "this size isn't sold".
    }

    this.sizeProductCache.set(handle, normalized);
    return normalized;
  }

  /**
   * Point each size button at its product for the current recommendation, then
   * apply availability, the usage-based pre-selection and pricing. The 12oz bag
   * is the recommended product itself; the bigger bags are fetched.
   */
  async initSizeOptions() {
    const product = this.recommendedProduct;
    if (!product) return;

    this.sizeProducts = { '12oz': product };

    // Keep the panel usable (12oz only) while the siblings are in flight.
    this.refreshSizeAvailability();
    this.applyUsageRecommendation();
    this.updatePricing();

    // Always ask for both siblings rather than trusting the `aa_size` tag —
    // a missing tag would silently hide a size that is genuinely for sale. The
    // cost is a cached 404 per size for coffees sold only in 12oz.
    const base = this.baseHandle(product);
    const biggerSizes = ['2lb', '5lb'];
    const siblings = await Promise.all(
      biggerSizes.map(size => this.fetchSizeProduct(base + QUIZ_SIZE_HANDLE_SUFFIX[size]))
    );

    // The customer may have gone back and changed an answer while the requests
    // were in flight, superseding this recommendation.
    if (this.recommendedProduct !== product) return;

    biggerSizes.forEach((size, i) => {
      if (siblings[i]) this.sizeProducts[size] = siblings[i];
    });

    this.refreshSizeAvailability();
    this.applyUsageRecommendation();
    this.updatePricing();
  }

  /**
   * The product behind a size, or null when that size isn't sold for this coffee.
   * @param {string} size
   * @returns {Object|null}
   */
  getSizeProduct(size) {
    return this.sizeProducts?.[size] || null;
  }

  /**
   * Whether a size can be bought in a purchase group. A size needs a product to
   * be bought at all, and a selling plan to be subscribed to.
   * @param {string} size
   * @param {string} prefix - 'subscription' or 'onetime'
   * @returns {boolean}
   */
  isSizeSelectable(size, prefix) {
    const sizeProduct = this.getSizeProduct(size);
    if (!sizeProduct || sizeProduct.available === false) return false;
    if (prefix === 'subscription') return (sizeProduct.sellingPlans || []).length > 0;
    return true;
  }

  /**
   * Disable the size buttons that can't be bought, and move the selection off a
   * size that has just become unselectable.
   */
  refreshSizeAvailability() {
    ['subscription', 'onetime'].forEach(prefix => {
      const selector = `[data-${prefix}-size]`;
      const buttons = Array.from(this.modal.querySelectorAll(selector));
      if (buttons.length === 0) return;

      buttons.forEach(btn => {
        const selectable = this.isSizeSelectable(btn.dataset.size, prefix);
        btn.disabled = !selectable;
        btn.setAttribute('aria-disabled', selectable ? 'false' : 'true');
      });

      const selected = buttons.find(btn => btn.classList.contains('is-selected'));
      if (!selected || selected.disabled) {
        const fallback = buttons.find(btn => !btn.disabled);
        if (fallback) this.selectRadioInGroup(fallback, selector);
      }
    });
  }

  /**
   * Largest size at or below `preferred` that can actually be bought in this
   * group, so a coffee without a 5lb bag still gets a sensible pre-selection.
   * @param {string} preferred
   * @param {string} prefix - 'subscription' or 'onetime'
   * @returns {string|null}
   */
  bestAvailableSize(preferred, prefix) {
    const upToPreferred = QUIZ_SIZE_ORDER.slice(0, QUIZ_SIZE_ORDER.indexOf(preferred) + 1);
    return upToPreferred.reverse().find(size => this.isSizeSelectable(size, prefix)) || null;
  }

  /**
   * Pre-select the subscription bag size, quantity and delivery frequency that
   * match the household's daily consumption (see QUIZ_USAGE_PLAN). The one-time
   * card gets the same size and quantity so the two options compare like for like.
   */
  applyUsageRecommendation() {
    const cupsPerDay = Math.min(8, Math.max(1, parseInt(this.answers.cupsPerDay) || 1));
    const plan = QUIZ_USAGE_PLAN[cupsPerDay] || QUIZ_USAGE_PLAN[1];

    // Decide once, then apply the same size and quantity to both cards so the
    // one-time and subscription options always compare like for like. Resolve
    // against the subscription card: a size it can take (product + selling
    // plan) is always buyable one-time too. Only a coffee with no subscribable
    // size at all falls back to what the one-time card can take.
    const size = this.bestAvailableSize(plan.size, 'subscription')
      || this.bestAvailableSize(plan.size, 'onetime');

    // When the planned size isn't sold for this coffee we fall back to a smaller
    // bag, so scale the quantity up to cover the same amount of coffee.
    const effectiveSize = size || '12oz';
    const quantity = effectiveSize === plan.size
      ? plan.quantity
      : Math.min(4, Math.max(1, Math.round(
          (QUIZ_CUPS_PER_BAG[plan.size] * plan.quantity) / QUIZ_CUPS_PER_BAG[effectiveSize]
        )));

    ['subscription', 'onetime'].forEach(prefix => {
      const sizeBtn = this.modal.querySelector(`[data-${prefix}-size][data-size="${effectiveSize}"]`);
      if (sizeBtn && !sizeBtn.disabled) this.selectRadioInGroup(sizeBtn, `[data-${prefix}-size]`);

      const qtyBtn = this.modal.querySelector(`[data-${prefix}-quantity][data-quantity="${quantity}"]`);
      if (qtyBtn) this.selectRadioInGroup(qtyBtn, `[data-${prefix}-quantity]`);
    });

    const frequencySelect = this.modal.querySelector('[data-subscription-frequency]');
    if (frequencySelect) frequencySelect.value = plan.frequency;

    this.updateUsageNote();
  }

  /**
   * Keep the "Based on your usage" note in step with what was pre-selected. It
   * deliberately reports the pre-selection, so it is not re-synced when the
   * customer changes the size themselves.
   */
  updateUsageNote() {
    const usageNote = this.modal.querySelector('[data-usage-note]');
    if (!usageNote) return;

    const cupsPerDay = parseInt(this.answers.cupsPerDay) || 1;
    const cupsSummary = usageNote.querySelector('[data-cups-summary]');
    if (cupsSummary) {
      cupsSummary.textContent = cupsPerDay === 1 ? '1 cup' : `${cupsPerDay} cups`;
    }

    const sizeSummary = usageNote.querySelector('[data-size-summary]');
    const selectedSize = this.modal.querySelector('[data-subscription-size].is-selected')?.dataset.size;
    if (sizeSummary && selectedSize) {
      sizeSummary.textContent = selectedSize;
    }
  }

  /**
   * Variant of a size's product matching the chosen grind. Size lives in the
   * product handle rather than the variant options, so only grind is matched.
   * @param {string} size
   * @param {string} grind
   * @returns {Object|null}
   */
  resolveVariant(size, grind) {
    const sizeProduct = this.getSizeProduct(size);
    if (!sizeProduct) return null;

    const grindWords = grind.replace(/-/g, ' ').toLowerCase();
    const matchesGrind = (v) =>
      (v.options || []).some(o => String(o).toLowerCase().includes(grindWords));
    const variants = sizeProduct.variants || [];

    return variants.find(v => matchesGrind(v) && v.available)
      || variants.find(matchesGrind)
      || variants.find(v => v.available)
      || variants[0]
      || null;
  }

  /**
   * Update pricing display. Sizes are separate products, so the price comes
   * from the selected size's own variant rather than a multiplier.
   */
  updatePricing() {
    if (!this.recommendedProduct) return;

    const ONE_TIME_SHIPPING = 7;

    [
      { prefix: 'subscription', shipping: 0 },
      { prefix: 'onetime', shipping: ONE_TIME_SHIPPING }
    ].forEach(({ prefix, shipping }) => {
      const size = this.modal.querySelector(`[data-${prefix}-size].is-selected`)?.dataset.size || '12oz';
      const quantity = parseInt(this.modal.querySelector(`[data-${prefix}-quantity].is-selected`)?.dataset.quantity) || 1;
      const grind = this.modal.querySelector(`[data-${prefix}-grind]`)?.value || 'whole-bean';

      const variant = this.resolveVariant(size, grind);
      const priceInCents = variant?.price
        ?? this.getSizeProduct(size)?.price
        ?? this.recommendedProduct.price;
      const subtotal = (priceInCents / 100) * quantity;

      const subtotalEl = this.modal.querySelector(`[data-${prefix}-subtotal]`);
      const totalEl = this.modal.querySelector(`[data-${prefix}-total]`);
      if (subtotalEl) subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
      if (totalEl) totalEl.textContent = `$${(subtotal + shipping).toFixed(2)}`;
    });
  }

  /**
   * Add product to cart
   */
  async addToCart(type) {
    if (this.isAddingToCart || !this.recommendedProduct) return;
    
    this.isAddingToCart = true;
    
    const isSubscription = type === 'subscription';
    const prefix = isSubscription ? 'subscription' : 'onetime';
    
    // Disable the button while processing. Capture the original markup so it can
    // be restored afterwards — the results page DOM persists between quiz runs,
    // so a button left in the "Adding..." state would stay stuck and block a
    // re-add when the same recommendation is shown again.
    const addButton = this.modal.querySelector(`[data-add-${type === 'subscription' ? 'subscription' : 'onetime'}]`);
    const originalButtonHtml = addButton?.innerHTML;
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = 'Adding...';
    }
    
    // Get selected options
    const size = this.modal.querySelector(`[data-${prefix}-size].is-selected`)?.dataset.size || '12oz';
    const grind = this.modal.querySelector(`[data-${prefix}-grind]`)?.value || 'whole-bean';
    const quantity = parseInt(this.modal.querySelector(`[data-${prefix}-quantity].is-selected`)?.dataset.quantity) || 1;
    
    // Each bag size is its own product, so both the variant and the selling plan
    // must come from the size's product — not from the 12oz recommendation.
    const sizeProduct = this.getSizeProduct(size) || this.recommendedProduct;
    const variant = this.resolveVariant(size, grind) || sizeProduct.variants?.[0];

    if (!variant) {
      this.announceToScreenReader('Sorry, that size is currently unavailable. Please choose another size.');
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = originalButtonHtml;
      }
      this.isAddingToCart = false;
      return;
    }
    
    // Prepare cart item
    const cartItem = {
      id: variant.id,
      quantity: quantity
    };
    
    // Add selling_plan for subscriptions (Shopify native subscriptions)
    const sellingPlans = sizeProduct.sellingPlans || [];
    if (isSubscription && sellingPlans.length > 0) {
      const frequency = this.modal.querySelector('[data-subscription-frequency]')?.value || '2-weeks';
      
      // Map frequency selection to selling plan
      // Common formats: "Every Week", "Every 2 Weeks", "Every 4 Weeks", etc.
      const frequencyMap = {
        '1-week': ['1 week', 'every week', 'weekly'],
        '2-weeks': ['2 week', 'every 2 week', 'bi-weekly', 'biweekly'],
        '3-weeks': ['3 week', 'every 3 week'],
        '4-weeks': ['4 week', 'every 4 week', 'monthly', 'month'],
        '6-weeks': ['6 week', 'every 6 week'],
        '8-weeks': ['8 week', 'every 8 week', '2 month']
      };
      
      const searchTerms = frequencyMap[frequency] || ['2 week'];
      
      // Find matching selling plan, falling back to the first one offered
      const sellingPlan = sellingPlans.find(plan => {
        const planNameLower = plan.name.toLowerCase();
        return searchTerms.some(term => planNameLower.includes(term));
      }) || sellingPlans[0];
      
      if (sellingPlan) {
        cartItem.selling_plan = sellingPlan.id;
      }
    }
    
    // Track add to cart
    this.trackEvent('quiz_add_to_cart', {
      product_id: sizeProduct.id,
      product_handle: sizeProduct.handle,
      variant_id: variant.id,
      purchase_type: type,
      size: size,
      quantity: quantity,
      selling_plan: cartItem.selling_plan || null
    });
    
    try {
      // Ask the add endpoint to re-render the cart drawer section(s) so the
      // native cart can morph in place. The native drawer (cart-items-component)
      // listens for the `cart:update` event and reads the rendered section HTML
      // from event.detail.data.sections; without this it opens showing stale
      // contents and the cart bubble count never updates.
      const cartItemsSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
        .filter(Boolean);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [cartItem],
          ...(cartItemsSectionIds.length ? { sections: cartItemsSectionIds.join(',') } : {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const cartData = await response.json();
      this.close();

      // Notify the native cart to refresh (morph the drawer section, update the
      // cart bubble count). Mirrors the CartAddEvent dispatched by product-form.js.
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: 'coffee-quiz',
          data: {
            source: 'coffee-quiz',
            itemCount: cartData?.item_count,
            sections: cartData?.sections
          }
        }
      }));

      // Open the native cart drawer
      const cartDrawer = document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
      if (cartDrawer?.open) cartDrawer.open();

    } catch (error) {
      console.error('Coffee Quiz: Error adding to cart:', error);
      // Re-enable and refocus the button BEFORE the alert so focus returns to it
      // (not <body>) when the alert is dismissed. On the success path focus is
      // intentionally left for the opened cart drawer, so this lives in catch.
      if (addButton) {
        addButton.disabled = false;
        if (originalButtonHtml != null) addButton.innerHTML = originalButtonHtml;
        addButton.focus();
      }
      alert('Sorry, there was an error adding to your cart. Please try again.');
    } finally {
      // Reset the button on both success and error so the results page can be
      // re-used to add the same product again on a later quiz run.
      if (addButton) {
        addButton.disabled = false;
        if (originalButtonHtml != null) addButton.innerHTML = originalButtonHtml;
      }
      this.isAddingToCart = false;
    }
  }

  /**
   * Track step completion
   */
  trackStepCompleted(stepName, answer) {
    const stepIndex = this.screens.indexOf(
      this.screens.find(s => s.includes(stepName.replace(/([A-Z])/g, '-$1').toLowerCase()))
    ) || this.currentScreenIndex;
    
    this.trackEvent('quiz_step_completed', {
      step: stepIndex,
      step_name: stepName,
      answer: answer
    });
  }

  /**
   * Track GA4 event
   */
  trackEvent(eventName, params = {}) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, { quiz_name: 'coffee_quiz', ...params });
    }
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({ event: eventName, ...params });
    }
  }
}

/**
 * Check if quiz is enabled in theme editor
 * Returns true if not in editor, or if enable_in_editor setting is true
 */
function isQuizEnabledInEditor() {
  // Always enabled outside of theme editor
  if (!window.Shopify?.designMode) return true;

  const modal = document.getElementById('coffee-quiz-modal');
  if (!modal) return false;

  // Check the enable_in_editor data attribute
  const enableInEditor = modal.getAttribute('data-enable-in-editor');
  return enableInEditor !== 'false';
}

// Initialize quiz when DOM is ready
function initCoffeeQuiz(forceNew = false) {
  // Skip initialization in theme editor if disabled
  if (window.Shopify?.designMode && !isQuizEnabledInEditor()) {
    if (window.coffeeQuiz?.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  if (forceNew || (window.Shopify && window.Shopify.designMode)) {
    window.coffeeQuiz = new CoffeeQuiz();
    return;
  }
  if (!window.coffeeQuiz) {
    window.coffeeQuiz = new CoffeeQuiz();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCoffeeQuiz());
} else {
  initCoffeeQuiz();
}

// Handle Shopify theme editor section reload
document.addEventListener('shopify:section:load', (e) => {
  const modal = e.target?.querySelector('#coffee-quiz-modal');
  if (!modal) return;

  // Skip if disabled in editor
  const enableInEditor = modal.getAttribute('data-enable-in-editor') !== 'false';
  if (window.Shopify?.designMode && !enableInEditor) {
    if (window.coffeeQuiz?.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  setTimeout(() => {
    const latestModal = document.getElementById('coffee-quiz-modal');
    if (window.Shopify?.designMode && latestModal?.getAttribute('data-enable-in-editor') === 'false') {
      if (window.coffeeQuiz?.modal?.open) {
        window.coffeeQuiz.close();
      }
      window.coffeeQuiz = null;
      return;
    }

    initCoffeeQuiz(true);
    if (window.coffeeQuiz && window.Shopify?.designMode) {
      window.coffeeQuiz.restoreEditorPosition();
    }
  }, 150);
});

// Handle theme editor preview screen changes
document.addEventListener('shopify:section:rerender', (e) => {
  const modal = e.target?.querySelector('#coffee-quiz-modal');
  if (!modal || !window.coffeeQuiz) return;
  
  // Skip if disabled in editor
  if (window.Shopify?.designMode && modal.getAttribute('data-enable-in-editor') === 'false') {
    if (window.coffeeQuiz.modal?.open) {
      window.coffeeQuiz.close();
    }
    window.coffeeQuiz = null;
    return;
  }
  
  const previewScreen = modal.dataset.previewScreen;
  if (previewScreen && previewScreen !== 'none') {
    const screenIndex = window.coffeeQuiz.screens.indexOf(previewScreen);
    if (screenIndex !== -1) {
      window.coffeeQuiz.modal.showModal();
      document.body.classList.add('coffee-quiz-open');
      window.coffeeQuiz.goToScreen(screenIndex);
    }
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CoffeeQuiz;
}
